"use client"

/**
 * Drafts entry on the creation-flow landing (step 0): a tile in the exact
 * visual language of CreationTypeStep's option tiles (rounded-xl p-4
 * bg-foreground/6, 11×11 icon circle on bg-sheet-handle/80), showing an
 * inbox icon + count. Expanding it reveals the same drafts list as the
 * My Listings tab (ListingDraftsSection — resume + delete, one canonical
 * list component, no fork).
 *
 * Renders nothing while loading/error/empty — the landing stays untouched
 * for sellers without drafts.
 */

import { ChevronDown, Inbox } from "lucide-react"
import { useTranslations } from "next-intl"
import { useState } from "react"

import { ListingDraftsSection } from "@/components/my-listings/listing-drafts-section"
import type { ListingDraftsState } from "@/hooks/use-listing-drafts"
import { cn } from "@/lib/utils"

interface CreationDraftsEntryProps {
  state: ListingDraftsState
  onResumeAction: (draftId: string) => void
  onDeleteAction: (draftId: string) => Promise<void>
}

export function CreationDraftsEntry({
  state,
  onResumeAction,
  onDeleteAction,
}: CreationDraftsEntryProps) {
  const t = useTranslations("listingCreation.draftsEntry")
  const [expanded, setExpanded] = useState(false)

  if (state.kind !== "ready" || state.drafts.length === 0) return null

  return (
    <div className="w-full min-w-0 pt-4">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-4 rounded-xl p-4 text-left transition-colors bg-foreground/6 hover:bg-foreground/10"
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sheet-handle/80">
          <Inbox className="h-5 w-5 text-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <span className="font-medium text-foreground">
            {t("title")}
            <span className="ml-1.5 text-foreground/50">({state.drafts.length})</span>
          </span>
          <p className="mt-0.5 text-xs text-foreground/50">{t("subtitle")}</p>
        </div>
        <ChevronDown
          className={cn(
            "h-5 w-5 shrink-0 text-foreground/50 transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>

      {expanded ? (
        <div className="mt-2">
          <ListingDraftsSection
            state={state}
            searchQuery=""
            onResumeAction={onResumeAction}
            onDeleteAction={onDeleteAction}
          />
        </div>
      ) : null}
    </div>
  )
}
